/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/parkingLotsApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API calls for Parking Lots (fetch list, fetch by ID, create, update, delete).
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { ParkingLot, ParkingLotPayload } from '../types';

export interface FetchParkingLotsParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface ParkingLotsListResponse {
  success: boolean;
  data: ParkingLot[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ParkingLotDetailResponse {
  success: boolean;
  data: ParkingLot;
  message?: string;
}



export async function fetchParkingLots(params: FetchParkingLotsParams = {}): Promise<ParkingLotsListResponse> {
  const queryParams = new URLSearchParams({
    page: (params.page || 1).toString(),
    limit: (params.limit || 10).toString(),
    sortBy: params.sortBy || 'name',
    sortOrder: params.sortOrder || 'asc',
    ...(params.search && { search: params.search })
  });

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/parking-lots?${queryParams}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch parking lots');
  }

  const result = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch parking lots');
  }

  return result;
}

export async function fetchParkingLotById(id: string | number): Promise<ParkingLot> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/parking-lots/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch parking lot');
  }

  const result: ParkingLotDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch parking lot');
  }

  return result.data;
}



export async function createParkingLot(payload: ParkingLotPayload): Promise<ParkingLot> {
  const response = await fetch(
    buildApiUrl('/api/telephony/parking-lots'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to create parking lot');
  }

  const result: ParkingLotDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to create parking lot');
  }

  return result.data;
}

export async function updateParkingLot(id: string | number, payload: ParkingLotPayload): Promise<ParkingLot> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/parking-lots/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to update parking lot');
  }

  const result: ParkingLotDetailResponse = await response.json();

  if (!result?.success) {
    throw new Error(result?.message || 'Failed to update parking lot');
  }

  return result.data;
}

export async function deleteParkingLot(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/parking-lots/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData?.message || 'Failed to delete parking lot');
  }
}

